/**
 * CustomParagraphNode extends Lexical's ParagraphNode to preserve custom CSS classes
 * during the conversion between view and edit modes.
 *
 * This is particularly important when working with utility classes that need to persist
 * through editing operations. For example, classes like 'u-size-xl' or 'u-text-center'
 * need to be maintained when a user starts editing content.
 *
 * Usage:
 * 1. Classes are stored in transformDOMStyles.ts using data-lexical-class
 * 2. CustomParagraphNode captures these classes during DOM import
 * 3. Classes are preserved during edit mode through the createDOM method
 */
import { DOMConversionMap, DOMConversionOutput, DOMExportOutput, LexicalEditor, ParagraphNode } from 'lexical';

export class CustomParagraphNode extends ParagraphNode {
  __customClassName: string;
  __customStyle: string;
  __customTagName: string;

  constructor(className: string = '', style: string = '', tagName: string = 'p', key?: string) {
    super(key);
    this.__customClassName = className;
    this.__customStyle = style;
    this.__customTagName = tagName;
  }

  static getType(): string {
    return 'custom-paragraph';
  }

  static getTagName(node: CustomParagraphNode): string {
    return node.__customTagName;
  }

  static clone(node: CustomParagraphNode): CustomParagraphNode {
    return new CustomParagraphNode(node.__customClassName, node.__customStyle, node.__customTagName, node.__key);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      h1: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h1'),
          };
        },
        priority: 1,
      }),
      h2: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h2'),
          };
        },
        priority: 1,
      }),
      h3: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h3'),
          };
        },
        priority: 1,
      }),
      h4: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h4'),
          };
        },
        priority: 1,
      }),
      h5: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h5'),
          };
        },
        priority: 1,
      }),
      h6: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'h6'),
          };
        },
        priority: 1,
      }),
      p: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'p'),
          };
        },
        priority: 1,
      }),
      div: () => ({
        conversion: (node: HTMLElement): DOMConversionOutput => {
          const className = node.dataset.lexicalClass || node.className || '';
          const style = node.dataset.lexicalStyle || node.getAttribute('style') || '';
          return {
            node: new CustomParagraphNode(className, style, 'div'),
          };
        },
        priority: 1,
      }),
    };
  }

  // Add after static importDOM()
  static importJSON(serializedNode: any): CustomParagraphNode {
    const node = new CustomParagraphNode(serializedNode.className, serializedNode.style, serializedNode.tagName);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  createDOM(): HTMLElement {
    const dom = document.createElement(this.__customTagName);

    if (this.__customClassName) {
      dom.className = this.__customClassName;
    }

    // Apply format type (alignment) directly
    const formatType = this.getFormatType();
    if (formatType) {
      dom.style.textAlign = formatType;

      // Also add the corresponding class for your CSS framework
      if (formatType === 'left') dom.classList.add('u-text-left');
      else if (formatType === 'center') dom.classList.add('u-text-center');
      else if (formatType === 'right') dom.classList.add('u-text-right');
      else if (formatType === 'justify') dom.classList.add('u-text-justify');
    } else if (this.__customStyle) {
      dom.setAttribute('style', this.__customStyle);
    }

    return dom;
  }
  exportDOM(editor: LexicalEditor): DOMExportOutput {
    let { element } = super.exportDOM(editor);

    if (element instanceof HTMLElement) {
      if (this.__customTagName === 'div') {
        const divElement = document.createElement('div');
        divElement.innerHTML = element.innerHTML;
        element = divElement;
      }

      if (this.__customClassName) {
        element.className = this.__customClassName;
      }

      const formatType = this.getFormatType();
      if (formatType) {
        element.style.textAlign = formatType;
      } else if (this.__customStyle) {
        element.setAttribute('style', this.__customStyle);
      }
    }

    return {
      element,
    };
  }

  // Add after exportDOM()
  exportJSON() {
    return {
      ...super.exportJSON(),
      className: this.__customClassName,
      style: this.__customStyle,
      tagName: this.__customTagName,
      type: 'custom-paragraph',
      version: 1,
    };
  }
}
